<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card bank-cheques-card">
          <div class="card-body">
            <div class="page-header-row">
              <div>
                <h5>Bank &amp; Cheques</h5>
                <p>Forecast clearance, manage cheques, and record daily bank balance.</p>
              </div>
              <div class="page-tabs">
                <button type="button" :class="{ active: activeTab === 'forecast' }" @click="switchTab('forecast')">Forecast</button>
                <button type="button" :class="{ active: activeTab === 'register' }" @click="switchTab('register')">Cheque Register</button>
                <button type="button" :class="{ active: activeTab === 'balance' }" @click="switchTab('balance')">Daily Balance</button>
              </div>
            </div>

            <!-- Tab 1: Forecast -->
            <div v-if="activeTab === 'forecast'">
              <div class="summary-card balance-summary">
                <span>Last Known Closing Balance</span>
                <strong>{{ formatCurrency(lastKnownBalance) }}</strong>
                <small v-if="latestBalanceDate">as of {{ formatDate(latestBalanceDate) }}</small>
              </div>

              <div v-if="!forecastLoading && forecastRows.length === 0" class="empty-state">
                No pending cheques
              </div>

              <div v-else class="table-responsive">
                <a-table
                  :columns="forecastColumns"
                  :data-source="forecastRows"
                  :loading="forecastLoading"
                  :pagination="false"
                  row-key="expected_clearance_date"
                  :row-class-name="forecastRowClass"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="['outgoing', 'incoming', 'net', 'min_balance_needed'].includes(column.key)">
                      {{ formatCurrency(record[column.key]) }}
                    </template>
                    <template v-else-if="column.key === 'expected_clearance_date'">
                      {{ formatDate(record.expected_clearance_date) }}
                    </template>
                    <template v-else-if="column.key === 'status'">
                      <span :class="forecastStatusClass(record)">{{ forecastStatusLabel(record) }}</span>
                    </template>
                  </template>
                </a-table>
              </div>
            </div>

            <!-- Tab 2: Cheque Register -->
            <div v-if="activeTab === 'register'">
              <div class="toolbar-row">
                <div class="filter-grid">
                  <div class="form-group">
                    <label>Status</label>
                    <select v-model="chequeFilters.status" class="form-control">
                      <option value="">All</option>
                      <option v-for="item in statusOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>Type</label>
                    <select v-model="chequeFilters.cheque_type" class="form-control">
                      <option value="">All</option>
                      <option value="ISSUED">Issued</option>
                      <option value="RECEIVED">Received</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>From</label>
                    <input v-model="chequeFilters.from" type="date" class="form-control" />
                  </div>
                  <div class="form-group">
                    <label>To</label>
                    <input v-model="chequeFilters.to" type="date" class="form-control" />
                  </div>
                  <div class="form-group action-group">
                    <label class="action-label">Actions</label>
                    <div class="button-group">
                      <button type="button" class="btn btn-submit" @click="loadCheques">View</button>
                      <button type="button" class="btn btn-outline-secondary" @click="resetChequeFilters">Reset</button>
                    </div>
                  </div>
                </div>
                <button v-if="canEdit" type="button" class="btn btn-submit add-btn" @click="openChequeModal">Add Cheque</button>
              </div>

              <div class="table-responsive">
                <a-table
                  :columns="chequeColumns"
                  :data-source="cheques"
                  :loading="chequeLoading"
                  :pagination="false"
                  row-key="chequeid"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'cheque_number'">
                      {{ record.cheque_number }}
                    </template>
                    <template v-else-if="column.key === 'cheque_type'">
                      <span :class="['type-badge', record.cheque_type === 'ISSUED' ? 'issued' : 'received']">
                        {{ record.cheque_type }}
                      </span>
                    </template>
                    <template v-else-if="column.key === 'amount'">
                      {{ formatCurrency(record.amount) }}
                    </template>
                    <template v-else-if="column.key === 'cheque_date'">
                      {{ formatDate(record.cheque_date) }}
                    </template>
                    <template v-else-if="column.key === 'expected_clearance_date'">
                      {{ formatDate(record.expected_clearance_date) }}
                    </template>
                    <template v-else-if="column.key === 'status'">
                      <span :class="['status-badge', statusBadgeClass(record.status)]">{{ record.status }}</span>
                    </template>
                    <template v-else-if="column.key === 'actions'">
                      <div v-if="record.status === 'PENDING' && canEdit" class="action-buttons">
                        <button type="button" class="btn btn-sm btn-success" @click="updateChequeStatus(record.chequeid, 'CLEARED')">Mark Cleared</button>
                        <button type="button" class="btn btn-sm btn-danger" @click="updateChequeStatus(record.chequeid, 'BOUNCED')">Bounced</button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="updateChequeStatus(record.chequeid, 'CANCELLED')">Cancel</button>
                      </div>
                      <span v-else>—</span>
                    </template>
                  </template>
                </a-table>
              </div>
            </div>

            <!-- Tab 3: Daily Balance -->
            <div v-if="activeTab === 'balance'">
              <form class="balance-form" @submit.prevent="saveDailyBalance">
                <div class="form-grid">
                  <div class="form-group">
                    <label>Date</label>
                    <input v-model="balanceForm.entry_date" type="date" class="form-control" :disabled="todayEntryExists" />
                  </div>
                  <div class="form-group">
                    <label>Closing Balance (₹)</label>
                    <input v-model="balanceForm.closing_balance" type="number" step="0.01" min="0" class="form-control" required :disabled="!canEdit" />
                  </div>
                  <div class="form-group">
                    <label>Remarks</label>
                    <input v-model="balanceForm.remarks" type="text" class="form-control" maxlength="250" :disabled="!canEdit" />
                  </div>
                  <div class="form-group action-group" v-if="canEdit">
                    <label class="action-label">&nbsp;</label>
                    <button type="submit" class="btn btn-submit" :disabled="balanceSaving">
                      {{ balanceSaving ? "Saving..." : "Save" }}
                    </button>
                  </div>
                </div>
                <div v-if="balanceSaved" class="save-confirmation">Saved</div>
              </form>

              <div class="table-responsive">
                <a-table
                  :columns="balanceColumns"
                  :data-source="balanceEntries"
                  :loading="balanceLoading"
                  :pagination="false"
                  row-key="id"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'entry_date'">
                      {{ formatDate(record.entry_date) }}
                    </template>
                    <template v-else-if="column.key === 'closing_balance'">
                      {{ formatCurrency(record.closing_balance) }}
                    </template>
                    <template v-else-if="column.key === 'entered_by_name'">
                      {{ record.entered_by_name || "—" }}
                    </template>
                    <template v-else>
                      {{ record[column.key] || "—" }}
                    </template>
                  </template>
                </a-table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Cheque Modal -->
    <div v-if="showChequeModal" class="modal-overlay" @click.self="closeChequeModal">
      <div class="modal-panel">
        <div class="modal-header">
          <h5>Add Cheque</h5>
          <button type="button" class="close-btn" @click="closeChequeModal">&times;</button>
        </div>
        <form @submit.prevent="submitCheque">
          <div class="type-toggle">
            <button
              type="button"
              :class="['toggle-btn', 'issued', { active: chequeForm.cheque_type === 'ISSUED' }]"
              @click="chequeForm.cheque_type = 'ISSUED'"
            >ISSUED</button>
            <button
              type="button"
              :class="['toggle-btn', 'received', { active: chequeForm.cheque_type === 'RECEIVED' }]"
              @click="chequeForm.cheque_type = 'RECEIVED'"
            >RECEIVED</button>
          </div>
          <div class="form-group">
            <label>Party Name *</label>
            <input v-model="chequeForm.party_name" type="text" class="form-control" required />
          </div>
          <div class="form-group">
            <label>Cheque Number *</label>
            <input v-model="chequeForm.cheque_number" type="text" class="form-control" required />
          </div>
          <div class="form-group">
            <label>Amount (₹) *</label>
            <input v-model="chequeForm.amount" type="number" step="0.01" min="0.01" class="form-control" required />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Cheque Date *</label>
              <input v-model="chequeForm.cheque_date" type="date" class="form-control" required />
            </div>
            <div class="form-group">
              <label>Expected Clearance Date *</label>
              <input v-model="chequeForm.expected_clearance_date" type="date" class="form-control" required />
            </div>
          </div>
          <div class="form-group">
            <label>Remarks</label>
            <input v-model="chequeForm.remarks" type="text" class="form-control" maxlength="250" />
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-outline-secondary" @click="closeChequeModal">Cancel</button>
            <button type="submit" class="btn btn-submit" :disabled="chequeSaving">{{ chequeSaving ? "Saving..." : "Add Cheque" }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { useStore } from "vuex";
import { computed } from "vue";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function daysAgoIso(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

const statusOptions = [
  { value: "PENDING", label: "Pending" },
  { value: "CLEARED", label: "Cleared" },
  { value: "BOUNCED", label: "Bounced" },
  { value: "CANCELLED", label: "Cancelled" },
];

export default {
  name: "bank-cheques",
  setup() {
    const store = useStore();
    const canEdit = computed(() => store.getters.hasPermission("finance.bank.edit"));
    return { canEdit };
  },
  data() {
    return {
      title: "Bank & Cheques",
      title1: "Forecast, register cheques, and track daily bank balance",
      activeTab: "forecast",
      lastKnownBalance: 0,
      latestBalanceDate: null,
      forecastRows: [],
      forecastLoading: false,
      forecastColumns: [
        { title: "Date", key: "expected_clearance_date", dataIndex: "expected_clearance_date" },
        { title: "Outgoing", key: "outgoing", dataIndex: "outgoing", align: "right" },
        { title: "Incoming", key: "incoming", dataIndex: "incoming", align: "right" },
        { title: "Net", key: "net", dataIndex: "net", align: "right" },
        { title: "Min Balance Needed", key: "min_balance_needed", dataIndex: "min_balance_needed", align: "right" },
        { title: "Status", key: "status" },
      ],
      cheques: [],
      chequeLoading: false,
      chequeFilters: { status: "", cheque_type: "", from: "", to: "" },
      statusOptions,
      chequeColumns: [
        { title: "Cheque No", key: "cheque_number", dataIndex: "cheque_number" },
        { title: "Party", dataIndex: "party_name", key: "party_name" },
        { title: "Type", key: "cheque_type", dataIndex: "cheque_type" },
        { title: "Amount", key: "amount", dataIndex: "amount", align: "right" },
        { title: "Cheque Date", key: "cheque_date", dataIndex: "cheque_date" },
        { title: "Clearance Date", key: "expected_clearance_date", dataIndex: "expected_clearance_date" },
        { title: "Status", key: "status", dataIndex: "status" },
        { title: "Actions", key: "actions" },
      ],
      showChequeModal: false,
      chequeSaving: false,
      chequeForm: {
        cheque_type: "ISSUED",
        party_name: "",
        cheque_number: "",
        amount: "",
        cheque_date: todayIso(),
        expected_clearance_date: todayIso(),
        remarks: "",
      },
      balanceForm: {
        entry_date: todayIso(),
        closing_balance: "",
        remarks: "",
      },
      balanceEntries: [],
      balanceLoading: false,
      balanceSaving: false,
      balanceSaved: false,
      todayEntryExists: false,
      balanceColumns: [
        { title: "Date", key: "entry_date", dataIndex: "entry_date" },
        { title: "Closing Balance", key: "closing_balance", dataIndex: "closing_balance", align: "right" },
        { title: "Remarks", dataIndex: "remarks", key: "remarks" },
        { title: "Entered By", key: "entered_by_name", dataIndex: "entered_by_name" },
      ],
    };
  },
  mounted() {
    this.loadForecast();
  },
  methods: {
    emptyChequeForm() {
      return {
        cheque_type: "ISSUED",
        party_name: "",
        cheque_number: "",
        amount: "",
        cheque_date: todayIso(),
        expected_clearance_date: todayIso(),
        remarks: "",
      };
    },
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === "forecast") this.loadForecast();
      if (tab === "register") this.loadCheques();
      if (tab === "balance") this.loadBalanceTab();
    },
    formatCurrency(value) {
      return `₹ ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    forecastStatusLabel(record) {
      return Number(record.min_balance_needed) > Number(this.lastKnownBalance) ? "⚠ Insufficient" : "✓ OK";
    },
    forecastStatusClass(record) {
      return Number(record.min_balance_needed) > Number(this.lastKnownBalance) ? "status-insufficient" : "status-ok";
    },
    forecastRowClass(record) {
      return Number(record.min_balance_needed) > Number(this.lastKnownBalance) ? "row-insufficient" : "";
    },
    statusBadgeClass(status) {
      const map = {
        PENDING: "pending",
        CLEARED: "cleared",
        BOUNCED: "bounced",
        CANCELLED: "cancelled",
      };
      return map[status] || "";
    },
    async loadLatestBalance() {
      try {
        const response = await axios.get("/v1/bank/daily-balance/latest");
        const data = response.data;
        if (data) {
          this.lastKnownBalance = Number(data.closing_balance || 0);
          this.latestBalanceDate = data.entry_date;
        } else {
          this.lastKnownBalance = 0;
          this.latestBalanceDate = null;
        }
      } catch (error) {
        this.lastKnownBalance = 0;
        this.latestBalanceDate = null;
      }
    },
    async loadForecast() {
      this.forecastLoading = true;
      try {
        await this.loadLatestBalance();
        const response = await axios.get("/v1/cheques/forecast");
        this.forecastRows = response.data || [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load forecast." });
      } finally {
        this.forecastLoading = false;
      }
    },
    buildChequeParams() {
      const params = {};
      if (this.chequeFilters.status) params.status = this.chequeFilters.status;
      if (this.chequeFilters.cheque_type) params.cheque_type = this.chequeFilters.cheque_type;
      if (this.chequeFilters.from) params.from = this.chequeFilters.from;
      if (this.chequeFilters.to) params.to = this.chequeFilters.to;
      return params;
    },
    async loadCheques() {
      this.chequeLoading = true;
      try {
        const response = await axios.get("/v1/cheques", { params: this.buildChequeParams() });
        this.cheques = response.data || [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load cheques." });
      } finally {
        this.chequeLoading = false;
      }
    },
    resetChequeFilters() {
      this.chequeFilters = { status: "", cheque_type: "", from: "", to: "" };
      this.loadCheques();
    },
    openChequeModal() {
      this.chequeForm = this.emptyChequeForm();
      this.showChequeModal = true;
    },
    closeChequeModal() {
      this.showChequeModal = false;
    },
    async submitCheque() {
      this.chequeSaving = true;
      try {
        await axios.post("/v1/cheques", {
          ...this.chequeForm,
          amount: Number(this.chequeForm.amount),
        });
        this.closeChequeModal();
        Swal.fire({ icon: "success", title: "Cheque added", timer: 1500, showConfirmButton: false });
        await this.loadCheques();
        if (this.activeTab === "forecast") await this.loadForecast();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to add cheque." });
      } finally {
        this.chequeSaving = false;
      }
    },
    async updateChequeStatus(chequeid, status) {
      const labels = { CLEARED: "mark as cleared", BOUNCED: "mark as bounced", CANCELLED: "cancel" };
      const result = await Swal.fire({
        title: "Confirm",
        text: `Do you want to ${labels[status]} this cheque?`,
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.patch(`/v1/cheques/${chequeid}/status`, { status });
        await this.loadCheques();
        if (this.activeTab === "forecast") await this.loadForecast();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to update cheque." });
      }
    },
    async loadBalanceTab() {
      this.balanceLoading = true;
      this.balanceSaved = false;
      try {
        const response = await axios.get("/v1/bank/daily-balance", {
          params: { from: daysAgoIso(6), to: todayIso() },
        });
        this.balanceEntries = response.data || [];
        const todayEntry = this.balanceEntries.find((entry) => entry.entry_date === todayIso());
        this.todayEntryExists = Boolean(todayEntry);
        this.balanceForm.entry_date = todayIso();
        if (todayEntry) {
          this.balanceForm.closing_balance = todayEntry.closing_balance;
          this.balanceForm.remarks = todayEntry.remarks || "";
        } else {
          this.balanceForm.closing_balance = "";
          this.balanceForm.remarks = "";
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load balance entries." });
      } finally {
        this.balanceLoading = false;
      }
    },
    async saveDailyBalance() {
      this.balanceSaving = true;
      this.balanceSaved = false;
      try {
        await axios.post("/v1/bank/daily-balance", {
          entry_date: this.balanceForm.entry_date,
          closing_balance: Number(this.balanceForm.closing_balance),
          remarks: this.balanceForm.remarks || null,
        });
        this.balanceSaved = true;
        await this.loadBalanceTab();
        setTimeout(() => { this.balanceSaved = false; }, 3000);
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to save balance." });
      } finally {
        this.balanceSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.bank-cheques-card {
  border: none;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.08);
}
.page-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.page-header-row h5 {
  margin-bottom: 4px;
}
.page-header-row p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}
.page-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.page-tabs button {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
}
.page-tabs button.active {
  background: #ff9f43;
  border-color: #ff9f43;
  color: #fff;
}
.summary-card {
  background: #f8fbff;
  border: 1px solid #e5eef8;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.summary-card strong {
  font-size: 24px;
  color: #111827;
}
.summary-card small {
  color: #6b7280;
}
.empty-state {
  text-align: center;
  padding: 40px 16px;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 8px;
}
.toolbar-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  flex: 1;
}
.add-btn {
  white-space: nowrap;
}
.type-badge.issued {
  color: #dc2626;
  font-weight: 600;
}
.type-badge.received {
  color: #16a34a;
  font-weight: 600;
}
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.status-badge.pending { background: #fef3c7; color: #92400e; }
.status-badge.cleared { background: #dcfce7; color: #166534; }
.status-badge.bounced { background: #fee2e2; color: #991b1b; }
.status-badge.cancelled { background: #f3f4f6; color: #4b5563; }
.status-ok { color: #16a34a; font-weight: 600; }
.status-insufficient { color: #dc2626; font-weight: 600; }
:deep(.row-insufficient) {
  background: #fef2f2 !important;
}
.action-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.balance-form {
  background: #f8fbff;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  align-items: end;
}
.save-confirmation {
  margin-top: 10px;
  color: #16a34a;
  font-weight: 600;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 16px;
}
.modal-panel {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  padding: 20px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.close-btn {
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  color: #6b7280;
}
.type-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.toggle-btn {
  flex: 1;
  border: 2px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  padding: 10px;
  font-weight: 700;
}
.toggle-btn.issued.active {
  border-color: #dc2626;
  background: #fef2f2;
  color: #dc2626;
}
.toggle-btn.received.active {
  border-color: #16a34a;
  background: #f0fdf4;
  color: #16a34a;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}
</style>
